'use strict';

const cart = require('../cart/cart');
const csrf = require('cisalfa/csrf/csrf');
const alertMessage = require('../utils/alertMessage');
const minicartPanel = require('../utils/minicartPanel');

const SELECTORS = {
    element: '.js-minicart',
    modal: '#minicartModal',
    modalContent: '.js-minicart-modal',
    otherModals: '.modal',
    cartContent: '.js-cart',
    title: '.js-minicart-title',
    toggle: '.js-minicart-toggle',
    proSubscription: '.js-pro-subscription',
    loginCta: '.js-step-login',
    quantity: '.js-minicart-qty',
    quantityLabel: '.js-minicart-qty-label',
    link: '.js-minicart-link',
    payBtn: '.js-paypal-button-on-cart-page',
    bonusModal: '#chooseBonusProductModal'
};

const CLASSES = {
    alertSuccess: 'cc-alert--success',
    sheet: 'cc-modal--sheet'
};

// Upper bound for another modal to finish its hide transition before the panel opens anyway
const OTHER_MODALS_CLOSE_TIMEOUT = 1000;

/**
 * Minicart component
 * @param {HTMLElement} element - The minicart element
 * @constructor
 * @returns {Object} - The minicart instance
 */
class Minicart {
    constructor(element) {
        this.element = element;
        this.url = element.data('action-url');
        this.quantity = element.find(SELECTORS.quantity);
        this.quantityLabel = element.find(SELECTORS.quantityLabel);
        this.link = $(SELECTORS.link);
        this.modal = $(SELECTORS.modal);
        this.modalContent = this.modal.find(SELECTORS.modalContent);

        // On mobile the panel confirming the add to cart is a bottom sheet (set here and not in the
        // modal template, which is part of the page-cached layout)
        if (minicartPanel.isEnabled()) {
            this.modal.addClass(CLASSES.sheet);
        }

        // Content already loaded and still matching the basket (reused by the header toggle)
        this.isContentStale = true;
        // Every load gets an id: only the latest one may render, show or hide the spinner
        this.requestId = 0;
        this.loadingRequestId = null;
        // In-flight GET reused by identical requests (e.g. hover prefetch followed by click)
        this.inFlight = null;
        // Panel to open once the bonus products dialog of the last add to cart is closed
        this.pendingBonusOpen = null;

        this.init();
        this.checkSubscriptionFlow();
    }

    /**
     * Initialize the minicart component
     * @memberof Minicart
     * @method init
     * @public
     */
    init() {
        this.element.on('count:update', (event, count) => {
            if (count && !Number.isNaN(count.quantityTotal)) {
                this.quantity.css('display', () => count.quantityTotal === 0 ? 'none' : 'inline-flex');
                this.quantity.text(count.quantityTotal);
                if (count.quantityTotal === 1) {
                    this.quantityLabel.addClass('cc-single').removeClass('cc-multiple');
                } else {
                    this.quantityLabel.addClass('cc-multiple').removeClass('cc-single');
                }
                this.link.attr({
                    'aria-label': count.minicartCountOfItems,
                    title: count.minicartCountOfItems
                });
            }
        });

        // Header toggle: present only when the panel does not open on add to cart
        // (otherwise the header icon is a plain link to the cart page)
        this.element.find(SELECTORS.toggle)
            .on('click', () => {
                this.open();
            })
            .on('mouseenter', () => {
                this.prefetch();
            });

        $('body').on('product:afterAddToCart', (e, response) => {
            this.onAfterAddToCart(response);
        });
        // Explicit request to open the panel, e.g. after adding the PRO subscription
        $('body').on('minicart:show', (e, options) => {
            this.markContentStale();
            this.open(options || {});
        });
        $('body').on('minicart:refresh', () => {
            this.markContentStale();
            this.open();
        });
        $('body').on('cart:update', () => {
            this.markContentStale();
        });
        // Never stack modals: any other modal opening closes the panel (e.g. login from the PRO banner)
        $('body').on('show.bs.modal', (e) => {
            if (e.target !== this.modal[0] && this.modal.hasClass('show')) {
                this.modal.modal('hide');
            }
        });
        $(document).on('hidden.bs.modal', SELECTORS.bonusModal, () => {
            this.onBonusChoiceClosed();
        });
    }

    /**
     * Confirms an add to cart: opens the panel when enabled, otherwise prefetches
     * the content for the header toggle (the alert is shown by the add to cart flow)
     * @memberof Minicart
     * @method onAfterAddToCart
     * @public
     * @param {Object} response - add to cart AJAX response (missing for some callers)
     */
    onAfterAddToCart(response) {
        this.markContentStale();
        this.pendingBonusOpen = null;

        if (!minicartPanel.isEnabled()) {
            // Defer the silent minicart prefetch off the interaction's critical path:
            // it competes with the AddToCart tracking cascade (GTM/Meta/TikTok/etc.)
            // for main-thread time right when INP is measured. requestIdleCallback
            // is not supported in Safari, hence the setTimeout fallback.
            const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
            schedule(() => this.prefetch());
            return;
        }

        const options = {
            added: true,
            pliUUID: response && response.pliUUID,
            fallbackMessage: response && response.message
        };

        if (response && !response.error && minicartPanel.hasBonusChoice(response)) {
            this.pendingBonusOpen = options;
            return;
        }

        if (minicartPanel.opensOnAddToCart(response)) {
            this.open(options);
        }
    }

    /**
     * Opens the panel for the last add to cart once its bonus products dialog is closed
     * (bonus chosen or dialog dismissed: the main product is in the basket either way)
     * @memberof Minicart
     * @method onBonusChoiceClosed
     * @public
     */
    onBonusChoiceClosed() {
        if (!this.pendingBonusOpen) {
            return;
        }
        const options = this.pendingBonusOpen;
        this.pendingBonusOpen = null;
        this.markContentStale();
        this.open(options);
    }

    /**
     * Marks the loaded content as outdated and drops the reusable in-flight request,
     * which may have been started before the basket changed
     * @memberof Minicart
     * @method markContentStale
     * @public
     */
    markContentStale() {
        this.isContentStale = true;
        this.inFlight = null;
    }

    /**
     * Builds the minicart content URL
     * @memberof Minicart
     * @method buildUrl
     * @public
     * @param {Object} options - open options
     * @returns {string} the URL
     */
    buildUrl(options) {
        const params = [];
        if (options.added) {
            params.push('added=true');
        }
        if (options.pliUUID) {
            params.push('pliuuid=' + encodeURIComponent(options.pliUUID));
        }
        if (!params.length) {
            return this.url;
        }
        return this.url + (this.url.indexOf('?') !== -1 ? '&' : '?') + params.join('&');
    }

    /**
     * Fetches the minicart content, reusing an identical in-flight request
     * @memberof Minicart
     * @method fetchContent
     * @public
     * @param {Object} options - open options
     * @returns {Object} - the jqXHR
     */
    fetchContent(options) {
        const url = this.buildUrl(options);
        if (this.inFlight && this.inFlight.url === url) {
            return this.inFlight.xhr;
        }

        const xhr = $.ajax({ url: url, method: 'GET', dataType: 'html' });
        this.inFlight = { url: url, xhr: xhr };
        xhr.always(() => {
            if (this.inFlight && this.inFlight.xhr === xhr) {
                this.inFlight = null;
            }
        });
        return xhr;
    }

    /**
     * Silently loads the content for the header toggle, if outdated
     * @memberof Minicart
     * @method prefetch
     * @public
     */
    prefetch() {
        // Never while the panel is open or opening: a new request id would discard that load
        if (!this.url || !this.isContentStale || this.loadingRequestId !== null || this.modal.hasClass('show')) {
            return;
        }
        const requestId = ++this.requestId;
        this.fetchContent({}).done((html) => {
            if (requestId === this.requestId) {
                this.renderContent(html);
            }
        });
    }

    /**
     * Loads (when needed) and shows the panel. Concurrent calls are safe: only the latest
     * one renders and shows, previous responses are discarded.
     * @memberof Minicart
     * @method open
     * @public
     * @param {Object} [options] - added: opened to confirm an add to cart;
     *                             pliUUID: line item just added (shown on top);
     *                             fallbackMessage: alert shown if the content cannot be loaded
     */
    open(options = {}) {
        if (!this.url) {
            return;
        }

        const requestId = ++this.requestId;
        const wasOpen = this.modal.hasClass('show');
        const needsContent = this.isContentStale || !!options.added;
        const content = needsContent ? this.fetchContent(options) : $.Deferred().resolve().promise();
        const otherModalsClosed = this.waitForOtherModals();

        this.loadingRequestId = requestId;
        // Callers usually stop the page spinner right after triggering the event that opens
        // the panel, in the same task: starting it in a microtask keeps it visible with no
        // gap, since microtasks run before the next paint. Skipped if already done.
        Promise.resolve().then(() => {
            if (this.loadingRequestId === requestId) {
                $.spinner().start();
            }
        });

        $.when(content, otherModalsClosed)
            .done((contentResult) => {
                if (requestId !== this.requestId) {
                    return;
                }
                if (needsContent) {
                    this.renderContent(contentResult[0]);
                }
                this.finishLoading(requestId);
                // A refresh of the open panel must not reopen it if the customer closed it meanwhile
                if (!wasOpen || this.modal.hasClass('show')) {
                    this.show();
                }
            })
            .fail(() => {
                if (requestId !== this.requestId) {
                    return;
                }
                this.finishLoading(requestId);
                if (options.fallbackMessage) {
                    alertMessage(options.fallbackMessage, CLASSES.alertSuccess, true);
                }
            });
    }

    /**
     * Stops the spinner started for a load
     * @memberof Minicart
     * @method finishLoading
     * @public
     * @param {number} requestId - id of the load
     */
    finishLoading(requestId) {
        if (this.loadingRequestId === requestId) {
            this.loadingRequestId = null;
            $.spinner().stop();
        }
    }

    /**
     * Resolves when no other modal is visible: modals still open are closed first, so the
     * panel never stacks on them (Bootstrap 4 would drop body.modal-open when they finish hiding).
     * Also waits for the panel itself if it is still running its hide transition.
     * @memberof Minicart
     * @method waitForOtherModals
     * @public
     * @returns {Object} - a promise
     */
    waitForOtherModals() {
        const deferred = $.Deferred();
        const panel = this.modal[0];
        const $closing = $(SELECTORS.otherModals).filter((index, modal) => {
            const $modal = $(modal);
            if (!$modal.is(':visible')) {
                return false;
            }
            return modal !== panel || !$modal.hasClass('show');
        });

        if (!$closing.length) {
            return deferred.resolve().promise();
        }

        let pending = $closing.length;
        const timeout = setTimeout(() => deferred.resolve(), OTHER_MODALS_CLOSE_TIMEOUT);
        $closing.each((index, modal) => {
            $(modal).one('hidden.bs.modal', () => {
                pending -= 1;
                if (!pending) {
                    clearTimeout(timeout);
                    deferred.resolve();
                }
            });
            if (modal !== panel) {
                $(modal).modal('hide');
            }
        });

        return deferred.promise();
    }

    /**
     * Shows the panel (no-op if already open)
     * @memberof Minicart
     * @method show
     * @public
     */
    show() {
        if (!this.modal.hasClass('show')) {
            this.modal.modal('show');
        }
    }

    /**
     * Replaces the panel content and binds its behaviours. If the open panel loses the focus
     * with it (focus inside the replaced content, or already dropped to <body> because the
     * focused control was removed, e.g. a removed product), it moves to the panel title.
     * @memberof Minicart
     * @method renderContent
     * @public
     * @param {string} html - minicart content markup
     */
    renderContent(html) {
        const active = document.activeElement;
        const focusIsLost = this.modal.hasClass('show')
            && (!active || active === document.body || $.contains(this.modalContent[0], active));

        this.modalContent.empty().append(html);
        this.isContentStale = false;

        cart.bindMinicart(this.modalContent.find(SELECTORS.cartContent).first());
        this.bindContent();
        this.initPaypalButton();

        if (focusIsLost) {
            this.modalContent.find(SELECTORS.title).first().trigger('focus');
        }
    }

    /**
     * Binds the PRO banner of the loaded content
     * @memberof Minicart
     * @method bindContent
     * @public
     */
    bindContent() {
        sessionStorage.setItem('subscriptionFlow', JSON.stringify({
            loginStep: false,
            cartStep: false
        }));

        this.modal.find(SELECTORS.proSubscription).off('click').on('click', (e) => {
            this.addSubscription($(e.currentTarget).attr('data-action'));
        });

        this.modal.find(SELECTORS.loginCta).off('click').on('click', () => {
            sessionStorage.setItem('subscriptionFlow', JSON.stringify({
                loginStep: false,
                cartStep: true
            }));
        });
    }

    /**
     * Adds the PRO subscription from the panel banner, then reloads the panel with it on top
     * @memberof Minicart
     * @method addSubscription
     * @public
     * @param {string} url - Cart-AddSubscription URL
     */
    async addSubscription(url) {
        $.spinner().start();

        let csrfToken;
        try {
            csrfToken = await csrf.methods.getCsrfToken();
        } catch (err) {
            console.error(err);
            $.spinner().stop();
            return;
        }

        $.ajax({
            url: url,
            method: 'POST',
            data: { csrf_token: csrfToken.token },
            success: (data) => {
                this.element.trigger('count:update', data);
                this.markContentStale();
                this.open({
                    // "Added" title only when the panel is the add to cart confirmation
                    added: minicartPanel.isEnabled() && !!(data && data.pliUUID),
                    pliUUID: data && data.pliUUID
                });
                $.spinner().stop();
            },
            error: (err) => {
                if (err.responseJSON && err.responseJSON.redirectUrl) {
                    window.location.href = err.responseJSON.redirectUrl;
                    return;
                }
                console.error(err);
                $.spinner().stop();
            }
        });
    }

    /**
     * Check subscription flow
     * @memberof Minicart
     * @method checkSubscriptionFlow
     * @public
     * @returns void
     */
    checkSubscriptionFlow () {
        if (sessionStorage.getItem('subscriptionFlow')) {
            const subscriptionFlow = JSON.parse(sessionStorage.getItem('subscriptionFlow'));
            if (subscriptionFlow && (!subscriptionFlow.cartStep || !subscriptionFlow.loginStep)) {
                sessionStorage.setItem('subscriptionFlow', JSON.stringify({
                    loginStep: false,
                    cartStep: false
                }));
                return;
            }
            this.open();
        }
    }

    /**
     * Initialize paypal button
     * @memberof Minicart
     * @method initPaypalButton
     * @public
     * @returns void
     */
    initPaypalButton () {
        if (!document.querySelector(SELECTORS.payBtn)) {
            return;
        }

        var PayPalCartModel = require('paypal/models/buttons/payPalCart');
        var payPalCartInstance = new PayPalCartModel(SELECTORS.payBtn);
        payPalCartInstance.initPayPalButton();
        return;
    }
}

module.exports = () => {
    cart();
    new Minicart($(SELECTORS.element).first());
};
